/**
 * Timing contracts two components have to agree on.
 *
 * Two of them bound the BROWSER's wait on a tRPC call, and carry the prefix
 * for that hop — `apps/api/src/lib/ai` has its own `REQUEST_TIMEOUT_MS` and
 * `MAX_REQUEST_TIMEOUT_MS` bounding API → provider. The last three are here for
 * the other reason a figure belongs in the shared layer: one side declares it
 * and the OTHER side has to be sized against it, so a copy in either package
 * would be the drift the contract exists to prevent.
 *
 * **A hung radio does not fail — it holds.** A phone whose modem is attached but
 * useless leaves `fetch` outstanding with nothing to reject, so without a
 * ceiling here the wait is unbounded and the confirm button reads "Saving…"
 * through all of it.
 *
 * **A ceiling only ever converts one failure into another**, and the one it
 * creates looks answerless: `isNoServerResponse` cannot tell it from a dropped
 * packet, which is why `isClientTimeout` exists and why `query-client.ts` uses
 * it to decline re-issuing a request we abandoned.
 */

/**
 * The ceiling for everything that is not a model call.
 *
 * A judgement, not a measurement: no p99 for this app's heaviest ordinary
 * request has been recorded. It bounds a driver's answerless retry chain at
 * roughly a minute and a half — three attempts of 30s plus backoff — rather
 * than never. Revisit it against real latency data rather than treating it as
 * derived.
 */
export const CLIENT_REQUEST_TIMEOUT_MS = 30_000

/**
 * The ceiling for a procedure whose answer waits on a language model.
 *
 * **The client must not give up before the SERVER does.** The server bounds its
 * own provider call deliberately and derives that bound from the feature's token
 * budget; this ceiling exists only to catch a dead radio, so it has to sit above
 * the largest bound the server can choose, plus the upload that happens first —
 * `estimator.fromPhotos` sends up to `MAX_ESTIMATOR_PHOTOS` ×
 * `MAX_ESTIMATOR_PHOTO_BASE64_CHARS` of base64 before the model is consulted.
 *
 * The SERVER half of that is machine-checked in
 * `apps/api/src/lib/model-backed-procedures.test.ts`, which computes the
 * ceiling for every registered feature — and the clamp the server could ever
 * choose — and fails if this number is not strictly greater. It is checked
 * rather than shared because the derivation is an API concern and does not
 * belong in a leaf package. The upload allowance is not expressed in that
 * check; it is headroom, not an asserted bound.
 *
 * A flat 180s was the first attempt here and was wrong: it sits *below* the
 * server's 230.4s for the 8192-token vision budget — the same number
 * `providers/anthropic.ts` already records as tried and rejected. Aborting there
 * is the expensive mistake, because the provider has been billed in full and the
 * answer is discarded.
 */
export const CLIENT_MODEL_REQUEST_TIMEOUT_MS = 720_000

/**
 * Every tRPC procedure whose handler can reach a model call, by full path.
 *
 * Membership gets the call the longer ceiling, and keeps it out of the same
 * batch as ordinary queries — which is what lets the two ceilings be enforced
 * independently. It does NOT make the call standalone: members of that link
 * still batch with each other within an operation type.
 *
 * Membership is established by nothing at runtime.
 * `apps/api/src/lib/model-backed-procedures.test.ts` guards what it can — every
 * path here resolves against the router, and every model-reaching FILE is
 * accounted for — and states there what it cannot.
 */
export const MODEL_BACKED_PROCEDURES = [
  'estimator.advise',
  'estimator.fromPhotos',
  'estimator.fromText',
  'userPallet.extractInventoryFromPhotos',
  'userPallet.getStorageAdvice',
  'venue.checkReturn',
] as const

const MODEL_BACKED: ReadonlySet<string> = new Set(MODEL_BACKED_PROCEDURES)

/** Whether this procedure's answer waits on a model. */
export function isModelBackedProcedure(path: string): boolean {
  return MODEL_BACKED.has(path)
}

/**
 * The most `idleTimeout` Bun accepts for a server
 * (`Bun.serve expects idleTimeout to be 255 or less`). A PER-REQUEST
 * `server.timeout` past it is accepted silently rather than refused (measured
 * on bun 1.3.12), so nothing at runtime holds a widened window under this —
 * deriving every window from the one constant is what does.
 *
 * Here rather than beside the worker routes: the API's model window derives
 * from it too, and that has nothing to do with the worker boundary.
 */
export const BUN_MAX_IDLE_TIMEOUT_SECONDS = 255

/**
 * How long a run of an `/internal/*` route may be in flight before the API says
 * so at `error`, repeatedly, until it finishes or the process restarts.
 *
 * It has to sit between two figures that live in the WORKER: above the worst
 * case of its retry ladder, so an ordinary slow run that is merely being
 * retried stays quiet; and below the cadence of the most frequent job, so a
 * run about to collide with its own next firing has already alarmed. Declared
 * here because the consumer is the API's and both bounds are the worker's —
 * `apps/worker/src/services/internal-call.test.ts` asserts the pair against its
 * own ladder and against the schedule, MEASURED (`shortestJobPeriodMs`).
 */
export const INTERNAL_RUN_STALE_MS = 45 * 60 * 1000

/**
 * How long a `pending` charge attempt speaks for the invoice it claimed.
 *
 * Billing's, and the reason it is not declared in billing: it is the figure the
 * WORKER's retry ladder is sized against. A run the API is killed mid-way
 * through leaves its claims `pending`, and an attempt arriving while they are
 * still inside this window is refused `in_flight` on every one of them — so it
 * re-runs only the customers the dead run had not reached, and the ones it
 * interrupted wait for the next night.
 *
 * `RETRY_DELAYS_MS` puts its LAST attempt past this, which makes recovery
 * reachable rather than routine: `callInternalRoute` returns on the first
 * success, so an earlier attempt that succeeds is still inside the window. See
 * that constant for the narrow condition; `internal-call.test.ts` asserts the
 * offset against this.
 *
 * Long enough to outlast Stripe's search indexing, which is what the reconcile
 * uses to tell "already charged" from "never charged".
 */
export const CHARGE_ATTEMPT_TTL_MS = 10 * 60 * 1000
